import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  IconButton,
  Flex,
  Spacer,
  Badge,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import CustomPromptModal from './CustomPromptModal';

import { CustomPrompt } from '../utils/geminiApi';

interface PromptManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPrompt: (prompt: CustomPrompt) => void;
  isPremium: boolean;
}

const PromptManager: React.FC<PromptManagerProps> = ({
  isOpen,
  onClose,
  onSelectPrompt,
  isPremium,
}) => {
  const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
  const [editingPrompt, setEditingPrompt] = useState<CustomPrompt | null>(null);
  const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  // ローカルストレージからプロンプトを読み込み
  useEffect(() => {
    const saved = localStorage.getItem('customPrompts');
    if (saved) {
      try {
        setCustomPrompts(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load custom prompts:', error);
      }
    }
  }, []);

  // プロンプトをローカルストレージに保存
  const savePrompts = (prompts: CustomPrompt[]) => {
    setCustomPrompts(prompts);
    localStorage.setItem('customPrompts', JSON.stringify(prompts));
  };

  const handleSavePrompt = (prompt: CustomPrompt) => {
    const existing = customPrompts.findIndex(p => p.id === prompt.id);
    if (existing >= 0) {
      const updated = [...customPrompts];
      updated[existing] = prompt;
      savePrompts(updated);
    } else {
      savePrompts([...customPrompts, prompt]);
    }
    setEditingPrompt(null);
  };

  const handleEditPrompt = (prompt: CustomPrompt) => {
    setEditingPrompt(prompt);
    onModalOpen();
  };

  const handleDeletePrompt = (id: string) => {
    setDeletingPromptId(id);
    onAlertOpen();
  };

  const confirmDelete = () => {
    if (deletingPromptId) {
      const updated = customPrompts.filter(p => p.id !== deletingPromptId);
      savePrompts(updated);
      setDeletingPromptId(null);
    }
    onAlertClose();
  };

  const handleCreateNew = () => {
    if (!isPremium) {
      toast({
        title: '有料プラン限定機能',
        description: 'カスタムプロンプトは有料プランでご利用いただけます',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingPrompt(null);
    onModalOpen();
  };

  const getGenreBadgeColor = (genre: string) => {
    const colors: Record<string, string> = {
      general: 'gray',
      blog: 'blue',
      twitter: 'cyan',
      meeting: 'green',
      idea: 'purple',
      summary: 'orange',
      custom: 'pink',
    };
    return colors[genre] || 'gray';
  };

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="blackAlpha.600"
      zIndex="1000"
      onClick={onClose}
    >
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="white"
        borderRadius="lg"
        p={6}
        maxW="600px"
        w="90%"
        maxH="80vh"
        overflowY="auto"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold">
            プロンプト管理
          </Text>
          <Spacer />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            size="sm"
            onClick={handleCreateNew}
            isDisabled={!isPremium}
          >
            新規作成
          </Button>
          <Button ml={3} onClick={onClose}>
            閉じる
          </Button>
        </Flex>

        {!isPremium && (
          <Box bg="orange.50" p={3} borderRadius="md" mb={4} border="1px" borderColor="orange.200">
            <Text fontSize="sm" color="orange.800">
              💡 有料プランにアップグレードすると、独自のプロンプトを作成・保存できます
            </Text>
          </Box>
        )}

        <VStack spacing={3} align="stretch">
          {customPrompts.length === 0 ? (
            <Box textAlign="center" py={8} color="gray.500">
              <Text>カスタムプロンプトがありません</Text>
              <Text fontSize="sm">「新規作成」ボタンで最初のプロンプトを作成しましょう</Text>
            </Box>
          ) : (
            customPrompts.map((prompt) => (
              <Box
                key={prompt.id}
                p={4}
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                _hover={{ bg: 'gray.50' }}
              >
                <Flex align="center" mb={2}>
                  <Text fontWeight="bold">{prompt.name}</Text>
                  <Spacer />
                  <Badge colorScheme={getGenreBadgeColor(prompt.genre)} mr={2}>
                    {prompt.genre}
                  </Badge>
                  <HStack spacing={1}>
                    <IconButton
                      aria-label="編集"
                      icon={<EditIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditPrompt(prompt)}
                      isDisabled={!isPremium}
                    />
                    <IconButton
                      aria-label="削除"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDeletePrompt(prompt.id)}
                      isDisabled={!isPremium}
                    />
                  </HStack>
                </Flex>
                <Text fontSize="sm" color="gray.600" noOfLines={2} mb={2}>
                  {prompt.prompt}
                </Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => onSelectPrompt(prompt)}
                >
                  このプロンプトを使用
                </Button>
              </Box>
            ))
          )}
        </VStack>

        <CustomPromptModal
          isOpen={isModalOpen}
          onClose={onModalClose}
          onSave={handleSavePrompt}
          editingPrompt={editingPrompt}
        />

        <AlertDialog
          isOpen={isAlertOpen}
          leastDestructiveRef={cancelRef}
          onClose={onAlertClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                プロンプトを削除
              </AlertDialogHeader>
              <AlertDialogBody>
                このプロンプトを削除してもよろしいですか？この操作は取り消せません。
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onAlertClose}>
                  キャンセル
                </Button>
                <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                  削除
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </Box>
  );
};

export default PromptManager;